import { Button } from "@msuite/ui/button";
import { ChartHeader } from "@msuite/ui/charts";
import { DashboardTile } from "@msuite/ui/dashboard";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import type { BookingsReviewListItem } from "@msuite/utils/types";
import { useState } from "react";
import { useNavigate } from "react-router";
import { usePartnerBookingSubmissions } from "@/hooks/use-partner-booking-submissions";
import { AssignmentReferenceBookingsModal } from "@/modals/partners/assignment-reference-bookings-modal";
import type { DashboardTileProps } from "../shared";
import { DashboardPartnerBookingDueItem } from "./dashboard-partner-booking-due-item";

/**
 * The days the organisation still owes hours on at its hosts. Offered by the layout only to an
 * organisation that is somebody's partner and to a caller with `read Partner`.
 */
export function DashboardPartnerBookingsDueItem({ item }: DashboardTileProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const navigate = useNavigate();
	const bookingsModal = useDisclosure();

	/** State */
	const [selected, setSelected] = useState<BookingsReviewListItem | null>(null);

	/** Query */
	const { open, isLoading, queryKey, refetch } = usePartnerBookingSubmissions();

	/** Functions */
	function handleOnOpen(booking: BookingsReviewListItem) {
		setSelected(booking);
		bookingsModal.onOpen();
	}

	async function handleOnComplete() {
		await refetch();
	}

	/** Render */
	return (
		<DashboardTile item={item}>
			<ChartHeader
				title={t("dashboard.partner_bookings_due")}
				description={t("dashboard.n_partner_bookings_due", { count: `${open.length}` })}
			/>
			<DashboardTile.List
				isLoading={isLoading}
				data={open}
				queryKey={queryKey}
			>
				{open.map((booking) => (
					<DashboardTile.List.Item
						key={booking.id}
						onClick={() => handleOnOpen(booking)}
						data-cy="dashboard-partner-booking-due"
					>
						<DashboardPartnerBookingDueItem booking={booking} />
					</DashboardTile.List.Item>
				))}
			</DashboardTile.List>
			<div className="absolute -right-5 -bottom-3">
				<Button
					size="xs"
					variant="white"
					icon={<Icon name="ArrowRight" />}
					onClick={() => navigate("/partners?partners-tabs=1")}
					data-cy="dashboard-partner-bookings-due-show-all"
				>
					{t("general.show_all")}
				</Button>
			</div>
			{bookingsModal.isOpen && selected && (
				<AssignmentReferenceBookingsModal
					isOpen={bookingsModal.isOpen}
					onClose={bookingsModal.onClose}
					referenceId={selected.id}
					partnershipId={selected.partnershipId}
					date={selected.date}
					onComplete={handleOnComplete}
				/>
			)}
		</DashboardTile>
	);
}
